import React, { useMemo } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import AgentDirectorySearch, { useDirectorySearchActions } from "@/components/AgentDirectorySearch";
import { DirectoryPanel, DirectorySkeleton } from "@/components/AgentDirectory";
import DirectoryBreadcrumb, {
  DIRECTORY_PATH,
  brokeragePath,
  glue,
  milesLabel,
  statePath,
  titleFromSlug,
  townPath,
} from "@/components/DirectoryBreadcrumb";
import { whereLabel } from "@/components/DirectoryBrokerageSheet";
import DirectoryResults from "@/components/DirectoryResults";
import { isNotFound, useDirectoryBrokerages, useDirectoryPlace, useDirectoryStates } from "@/hooks/useAgentDirectory";
import "./DirectoryTownView.scss";
import AppLink from "@/components/AppLink";

const MAX_NEARBY = 12;
const MAX_BROKERAGES = 12;

export const stateNameFor = (states, stateSlug, fallback) =>
  (states || []).find((state) => state.slug === String(stateSlug || "").toLowerCase())?.name ||
  fallback ||
  String(stateSlug || "").toUpperCase();

const NearbyTowns = ({ nearby, stateSlug, stateName }) => (
  <section className="directory-section" aria-labelledby="town-nearby">
    <h2 id="town-nearby">Nearby cities</h2>
    {nearby.length ? (
      <ul className="directory-nearby">
        {nearby.slice(0, MAX_NEARBY).map((town) => (
          <li key={town.slug}>
            <AppLink href={townPath(stateSlug, town.slug)}>
              <span className="directory-nearby__name">{town.name}</span>
              <span className="directory-nearby__miles">{milesLabel(town.miles)}</span>
            </AppLink>
          </li>
        ))}
      </ul>
    ) : null}
    <AppLink className="directory-section__more" href={statePath(stateSlug)}>
      {glue(`All cities in ${stateName}`)}
    </AppLink>
  </section>
);

const NearbyBrokerages = ({ location, place }) => {
  const query = useDirectoryBrokerages(location, "");
  const brokerages = (query.data?.pages?.[0]?.brokerages || []).slice(0, MAX_BROKERAGES);
  if (query.isError || (!query.isLoading && !brokerages.length)) return null;
  return (
    <section className="directory-section" aria-labelledby="town-brokerages">
      <h2 id="town-brokerages">{glue(`Brokerages in and near ${place.name}`)}</h2>
      <ul className="directory-offices" aria-busy={query.isLoading}>
        {query.isLoading
          ? Array.from({ length: 6 }, (_, index) => (
              <li key={index} aria-hidden="true">
                <span className="directory-offices__card is-loading">
                  <span className="directory-offices__bar" />
                  <span className="directory-offices__bar is-short" />
                </span>
              </li>
            ))
          : brokerages.map((brokerage) => (
              <li key={`${brokerage.name}-${brokerage.town || ""}`}>
                <AppLink className="directory-offices__card" href={brokeragePath(brokerage.name, place)}>
                  <span className="directory-offices__name">{brokerage.name}</span>
                  {whereLabel(brokerage, place.name) ? (
                    <span className="directory-offices__where">{whereLabel(brokerage, place.name)}</span>
                  ) : null}
                </AppLink>
              </li>
            ))}
      </ul>
    </section>
  );
};

const HeadSkeleton = () => (
  <div className="directory-town__head-skeleton" aria-hidden="true">
    <span className="directory-town__bar is-crumb" />
    <span className="directory-town__bar is-title" />
    <span className="directory-town__bar is-line" />
  </div>
);

const DirectoryTownView = ({ stateSlug, townSlug }) => {
  const actions = useDirectorySearchActions();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const placeQuery = useDirectoryPlace(stateSlug, townSlug);
  const statesQuery = useDirectoryStates();
  const place = placeQuery.data?.place;
  const nearby = placeQuery.data?.nearby || [];
  const stateCode = String(place?.state || stateSlug || "").toUpperCase();
  const stateName = stateNameFor(statesQuery.data?.states, stateSlug, place?.stateName);
  const location = useMemo(
    () =>
      place
        ? { city: place.name, state: stateCode, latitude: Number(place.lat), longitude: Number(place.long) }
        : null,
    [place, stateCode],
  );

  const onSearchText = (text) => {
    const next = new URLSearchParams(params);
    next.set("q", text);
    next.delete("brokerage");
    setParams(next, { replace: true });
  };

  if (placeQuery.isError && !place) {
    const missing = isNotFound(placeQuery.error);
    const townName = titleFromSlug(townSlug);
    return (
      <>
        <Helmet>
          <title>Agent directory | Gitcha</title>
          {missing ? <meta name="robots" content="noindex" /> : null}
        </Helmet>
        <DirectoryPanel
          title={missing ? glue(`Gitcha does not list agents in ${townName} yet`) : "This town did not load"}
          body={
            missing
              ? `Pick a nearby town from the towns Gitcha serves in ${stateName}.`
              : "Check your connection and try again."
          }
          actions={
            missing
              ? [
                  { label: `All cities in ${stateName}`, href: statePath(stateSlug) },
                  { label: "Agent directory", href: DIRECTORY_PATH },
                ]
              : [
                  { label: "Try again", onClick: () => placeQuery.refetch() },
                  { label: "Agent directory", href: DIRECTORY_PATH },
                ]
          }
        />
      </>
    );
  }

  if (!place) {
    return (
      <>
        <HeadSkeleton />
        <DirectorySkeleton />
      </>
    );
  }

  const label = `${place.name}, ${stateCode}`;
  const trail = [
    { label: "Agents", href: DIRECTORY_PATH },
    { label: stateName, href: statePath(stateSlug) },
    { label: place.name, href: townPath(stateSlug, townSlug) },
  ];

  return (
    <>
      <Helmet>
        <title>{`Real estate agents in and near ${label} | Gitcha agent directory`}</title>
        <meta
          name="description"
          content={`Real estate agents in and near ${label} on Gitcha, with the buyer demand each one is working with. Agents working with buyers right now are listed first.`}
        />
      </Helmet>
      <DirectoryBreadcrumb trail={trail} />
      <header className="directory-page__head">
        <h1>{glue(`Agents in and near ${label}`)}</h1>
        <p>Agents based in {place.name} come first, then the nearest agents around&nbsp;it.</p>
      </header>

      <AgentDirectorySearch
        {...actions}
        place={label}
        location={location}
        onSearchText={onSearchText}
        onPickBrokerage={(name) => navigate(brokeragePath(name, { ...place, state: stateCode }))}
      />

      <DirectoryResults
        location={location}
        placeSlug={`${stateSlug}/${townSlug}`}
        placeName={place.name}
        scopeLabel={label}
      />

      <div className="directory-town__more">
        <NearbyTowns nearby={nearby} stateSlug={stateSlug} stateName={stateName} />
        <NearbyBrokerages location={location} place={{ ...place, state: stateCode }} />
      </div>
    </>
  );
};

export default DirectoryTownView;
